import { cancelable } from 'cancelable-promise';
import { debounce } from 'lodash-es';
import { extract, parse, stringify } from 'query-string';
import { LAYOUT, PDF_PRELOAD_STRATEGY, PDF_RENDERER, PDF_RENDER_STRATEGY } from '../../constants/Constants';
import { AssetViewerViewMapper } from '../../helpers/AssetViewerViewMapper';
import { browser } from '../../helpers/BrowserHelper';
import { PromiseHelper } from '../../helpers/PromiseHelper';
import { EmbeddedDataMediaPlayOptionsRepeat, EmbeddedDataMediaType } from '../../types/EmbeddedData.type';
import { RenderState } from '../../types/RenderState.type';
import { ViewerEventType, ViewerPreviewRenderedEvent } from '../../types/ViewerEvents.type';
import { capDimensions, clamp, mapRange } from '../../utils/math';
import { millisecondsToSeconds } from '../../utils/time';
const PDFJS_CANVAS_CLASS = 'pdfjs-canvas';
export class PdfJsPresentationRenderer {
    constructor(config, assetViewerView, pdfJsLinkService, presentation, lowQualityPreviewPresentation, highQualityPreviewPresentation) {
        this.config = config;
        this.assetViewerView = assetViewerView;
        this.pdfJsLinkService = pdfJsLinkService;
        this.presentation = presentation;
        this.lowQualityPreviewPresentation = lowQualityPreviewPresentation;
        this.highQualityPreviewPresentation = highQualityPreviewPresentation;
        this.isLoading = false;
        this.pdfDocumentPromise = null;
        this.pdfPagePromise = null;
        this.renderHighQualityPreviewPromise = null;
        this.renderLowQualityPreviewPromise = null;
        this.renderLowAndHighQualityPreviewPromise = null;
        this.renderPromise = null;
        this.highQualityImageLoaded = false;
        this.lowQualityImageLoaded = false;
        this.pdfCanvasLoaded = false;
        this.pdfDocument = null;
        this.pdfPage = null;
        this.operatorList = null;
        this.textLayerDiv = null;
        this.annotationLayerDiv = null;
        this.isActive = false;
        this.renderTask = null;
        this.containerWidth = null;
        this.containerHeight = null;
        this.defaultViewport = null;
        this.scale = 1;
        this.pinchZoomLayer = null;
        this.embeddedMediaLayer = null;
        this.annotationCanvasMap = new Map();
        this.onAnnotationLayerDivClick = (event) => {
            const target = event.target;
            if (target.tagName === 'A' && typeof target.href === 'string' && target.href.indexOf('showpad://') === 0) {
                event.preventDefault();
                this.pdfJsLinkService.handleShowpadLink(target.href);
            }
        };
        this.onPreviewError = this.onPreviewError.bind(this);
        this.onAnnotationLayerError = this.onAnnotationLayerError.bind(this);
    }
    get url() {
        const pageIndex = (this.presentation.details && this.presentation.details.firstPageIndex) || 0;
        return this.presentation.urls[pageIndex];
    }
    get highQualityPreviewUrl() {
        const pageIndex = this.highQualityPreviewPresentation &&
            this.highQualityPreviewPresentation.details &&
            this.highQualityPreviewPresentation.details.firstPageIndex === 1
            ? this.assetViewerView.page
            : this.assetViewerView.page - 1;
        const url = this.highQualityPreviewPresentation && this.highQualityPreviewPresentation.urls[pageIndex];
        if (!url) {
            return null;
        }
        const queryParams = parse(extract(url));
        // Variable size results in cache busting!
        // queryParams['size'] = `${this.container.clientWidth * window.devicePixelRatio}x${this.container.clientWidth * window.devicePixelRatio}`;
        queryParams['size'] = '1600x1600';
        return url.split('?')[0] + '?' + stringify(queryParams);
    }
    get lowQualityPreviewUrl() {
        const pageIndex = this.lowQualityPreviewPresentation &&
            this.lowQualityPreviewPresentation.details &&
            this.lowQualityPreviewPresentation.details.firstPageIndex === 1
            ? this.assetViewerView.page
            : this.assetViewerView.page - 1;
        const url = this.lowQualityPreviewPresentation && this.lowQualityPreviewPresentation.urls[pageIndex];
        if (!url) {
            return null;
        }
        const queryParams = parse(extract(url));
        // Variable size results in cache busting!
        // queryParams['size'] = `${this.container.clientWidth * window.devicePixelRatio}x${this.container.clientWidth * window.devicePixelRatio}`;
        queryParams['size'] = '1600x1600';
        return url.split('?')[0] + '?' + stringify(queryParams);
    }
    get width() {
        if (this._width) {
            return this._width;
        }
        const presentation = [this.presentation, this.highQualityPreviewPresentation, this.lowQualityPreviewPresentation].find(presentation => !!(presentation && presentation.details && presentation.details.dimensions && presentation.details.dimensions.width));
        if (presentation) {
            const pageIndex = presentation.details.firstPageIndex === 1 ? this.assetViewerView.page : this.assetViewerView.page - 1;
            const exceptions = presentation.details.dimensions.exceptions;
            return exceptions && exceptions[pageIndex] ? exceptions[pageIndex].width : presentation.details.dimensions.width;
        }
    }
    get height() {
        if (this._height) {
            return this._height;
        }
        const presentation = [this.presentation, this.highQualityPreviewPresentation, this.lowQualityPreviewPresentation].find(presentation => !!(presentation && presentation.details && presentation.details.dimensions && presentation.details.dimensions.height));
        if (presentation) {
            const pageIndex = presentation.details.firstPageIndex === 1 ? this.assetViewerView.page : this.assetViewerView.page - 1;
            const exceptions = presentation.details.dimensions.exceptions;
            return exceptions && exceptions[pageIndex] ? exceptions[pageIndex].height : presentation.details.dimensions.height;
        }
    }
    set width(value) {
        this._width = value;
    }
    set height(value) {
        this._height = value;
    }
    get dimensions() {
        return this.width && this.height ? { width: this.width, height: this.height } : null;
    }
    get hasMetaData() {
        return !!this.dimensions;
    }
    get embeddedData() {
        return this.assetViewerView.assetViewerItem.embeddedData;
    }
    loadPdfJsResources() {
        return this.config.getPdfJs();
    }
    loadPdfJsDocument() {
        if (!this.pdfDocumentPromise) {
            this.pdfDocumentPromise = this.loadPdfJsResources()
                .then(result => {
                if (!this.assetViewerView.assetViewerItem.worker ||
                    (this.assetViewerView.assetViewerItem.worker && this.assetViewerView.assetViewerItem.worker.destroyed)) {
                    this.assetViewerView.assetViewerItem.worker = this.config.getPdfJsWorker();
                }
                const contentLength = this.presentation.details && this.presentation.details.fileSize;
                const pageCount = this.assetViewerView.assetViewerItem.numViews;
                const dynamicRangeChunkSize = contentLength && pageCount
                    ? Math.round(Math.max(Math.min(contentLength / pageCount, this.config.PDFJS_MAX_RANGE_CHUNK_SIZE), this.config.PDFJS_MIN_RANGE_CHUNK_SIZE))
                    : undefined;
                const rangeChunkSize = this.config.PDFJS_RANGE_CHUNK_SIZE || dynamicRangeChunkSize;
                const getDocumentConfig = {
                    url: this.url,
                    worker: this.assetViewerView.assetViewerItem.worker,
                    withCredentials: true,
                    contentLength,
                    length: contentLength,
                    rangeChunkSize,
                    disableRange: false,
                    disableAutoFetch: true,
                    disableStream: true,
                    cMapUrl: result.versionConfig.cMapUrl,
                    cMapPacked: true
                };
                return this.assetViewerView.assetViewerItem.loadPdf(getDocumentConfig);
            })
                .then(pdfDocument => {
                this.pdfDocument = pdfDocument;
                return pdfDocument;
            });
        }
        return this.pdfDocumentPromise;
    }
    loadPdfJsPage() {
        if (!this.pdfPagePromise) {
            this.pdfPagePromise = cancelable(this.loadPdfJsDocument()
                .then(pdfDocument => {
                let page;
                if (this.pdfDocument.numPages > 1 &&
                    this.assetViewerView.assetViewerItem.page &&
                    !this.assetViewerView.assetViewerItem.pageRanges) {
                    // single page out of multipage original document
                    page = this.assetViewerView.assetViewerItem.page;
                }
                else if (this.assetViewerView.assetViewerItem.page && !this.assetViewerView.assetViewerItem.pageRanges) {
                    // single page and processed single page document
                    page = 1;
                }
                else {
                    page = this.assetViewerView.page; // page out of mulitpage original document
                }
                return pdfDocument.getPage(page);
            })
                .then(pdfPage => {
                this.pdfPage = pdfPage;
                this.defaultViewport = pdfPage.getViewport({ scale: 1 });
                this.width = this.defaultViewport.width;
                this.height = this.defaultViewport.height;
                return pdfPage;
            })
                .then(pdfPage => pdfPage.getOperatorList())
                .then(operatorList => {
                this.operatorList = operatorList;
                return this.pdfPage;
            }));
        }
        return this.pdfPagePromise;
    }
    load(width = null, height = null, skipLowPreview = false, skipHighPreview = false) {
        // if width and height is available from the api, load is bypassed
        this.isLoading = true;
        if (!skipLowPreview && this.lowQualityPreviewPresentation && this.lowQualityPreviewUrl) {
            return cancelable(this.loadLowQualityImage(this.lowQualityPreviewUrl)
                .then(image => {
                this.isLoading = false;
                this.width = image.naturalWidth;
                this.height = image.naturalHeight;
                return {
                    width: image.naturalWidth,
                    height: image.naturalHeight
                };
            })
                .catch(() => this.load(null, null, true)));
        }
        if (!skipHighPreview && this.highQualityPreviewPresentation && this.highQualityPreviewUrl) {
            return cancelable(this.loadHighQualityImage(this.highQualityPreviewUrl)
                .then(image => {
                this.isLoading = false;
                this.width = image.naturalWidth;
                this.height = image.naturalHeight;
                return {
                    width: image.naturalWidth,
                    height: image.naturalHeight
                };
            })
                .catch(() => this.load(null, null, true, true)));
        }
        return this.loadPdfJsPage().then(() => {
            this.isLoading = false;
            return {
                width: this.width,
                height: this.height,
                // only add views if we're loading the first page of a document and the assetViewerItem is not a single page
                numViews: this.assetViewerView.page === 1 && !this.assetViewerView.assetViewerItem.page ? this.pdfDocument.numPages : null
            };
        });
    }
    addHighQualityPreviewImageTagToDOM(url, container) {
        // Don't add a high quality image if the pdfCanvas is already loaded
        if (this.pdfCanvasLoaded) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            this.highQualityImage = new Image();
            this.highQualityImage.src = url;
            this.highQualityImage.classList.add('hidden-until-loaded');
            this.highQualityImage.onload = () => {
                this.highQualityImage.classList.remove('hidden-until-loaded');
                this.highQualityImageLoaded = true;
                resolve(this.highQualityImage);
            };
            this.highQualityImage.onerror = error => {
                reject(error);
            };
            if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
            }
            container.appendChild(this.highQualityImage);
        });
    }
    addLowQualityPreviewImageTagToDOM(url, container) {
        // Don't add the low quality image if the high quality image is already loaded
        if (this.highQualityImageLoaded || this.pdfCanvasLoaded) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            this.lowQualityImage = new Image();
            this.lowQualityImage.src = url;
            this.lowQualityImage.classList.add('hidden-until-loaded');
            this.lowQualityImage.onerror = error => {
                reject(error);
            };
            this.lowQualityImage.onload = () => {
                this.lowQualityImage.classList.remove('hidden-until-loaded');
                this.lowQualityImageLoaded = true;
                this.onPreviewRendered();
                resolve(this.lowQualityImage);
            };
            if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                this.lowQualityImage.addEventListener('contextmenu', e => e.preventDefault());
            }
            container.appendChild(this.lowQualityImage);
        });
    }
    loadLowQualityImage(url) {
        return new Promise((resolve, reject) => {
            this.lowQualityImage = new Image();
            this.lowQualityImage.src = url;
            this.lowQualityImage.onload = () => {
                this.lowQualityImageLoaded = true;
                resolve(this.lowQualityImage);
            };
            this.lowQualityImage.onerror = error => reject(error);
        });
    }
    loadHighQualityImage(url) {
        return new Promise((resolve, reject) => {
            this.highQualityImage = new Image();
            this.highQualityImage.src = url;
            this.highQualityImage.onload = () => {
                this.highQualityImageLoaded = true;
                resolve(this.highQualityImage);
            };
            this.highQualityImage.onerror = error => reject(error);
        });
    }
    renderHighQualityPreview(container, url) {
        if (!this.renderHighQualityPreviewPromise) {
            this.renderHighQualityPreviewPromise = Promise.resolve()
                .then(() => this.addHighQualityPreviewImageTagToDOM(url, container))
                .then(() => {
                this.onPreviewRendered();
            });
        }
        return this.renderHighQualityPreviewPromise;
    }
    renderLowQualityPreview(container, url) {
        if (!this.renderLowQualityPreviewPromise) {
            this.renderLowQualityPreviewPromise = Promise.resolve().then(() => this.addLowQualityPreviewImageTagToDOM(url, container));
        }
        return this.renderLowQualityPreviewPromise;
    }
    renderLowAndHighQualityPreview(container, highQualityUrl, lowQualityUrl) {
        if (!this.renderLowAndHighQualityPreviewPromise) {
            this.renderLowAndHighQualityPreviewPromise = this.renderLowQualityPreview(container, lowQualityUrl)
                .then(() => this.loadHighQualityImage(highQualityUrl))
                .then(() => {
                container.appendChild(this.highQualityImage);
                if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                    this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                }
                this.onPreviewRendered();
                container.removeChild(this.lowQualityImage);
            });
        }
        return this.renderLowAndHighQualityPreviewPromise;
    }
    renderPdfJs(container) {
        return this.loadPdfJsPage().then(pdfPage => {
            switch (this.config.PDF_RENDERER) {
                case PDF_RENDERER.CANVAS:
                    return this.renderPdfPage(pdfPage, container);
                default:
                    throw new Error('unknown PDF_RENDERER specified in Config');
            }
        });
    }
    renderPreview(container) {
        if (this.config.PDF_PRELOAD_STRATEGY === PDF_PRELOAD_STRATEGY.LOW_PREVIEW && this.lowQualityPreviewUrl) {
            return this.renderLowQualityPreview(container, this.lowQualityPreviewUrl);
        }
        if (this.config.PDF_PRELOAD_STRATEGY === PDF_PRELOAD_STRATEGY.HIGH_PREVIEW && this.highQualityPreviewUrl) {
            return this.renderHighQualityPreview(container, this.highQualityPreviewUrl);
        }
        return Promise.resolve();
    }
    render(container) {
        this.container = container;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        // TODO Find more elegent solution @Julian (If container is same size we do not need to re-render)
        if (containerWidth === this.containerWidth && containerHeight === this.containerHeight) {
            return cancelable(Promise.resolve());
        }
        this.containerWidth = containerWidth;
        this.containerHeight = containerHeight;
        if (this.pdfPagePromise) {
            this.pdfPagePromise.cancel();
            this.pdfPagePromise = null;
        }
        if (this.renderPromise) {
            this.renderPromise.cancel();
        }
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
        if (this.embeddedMediaLayer) {
            this.embeddedMediaLayer.destroy();
            this.embeddedMediaLayer = null;
        }
        if (this.highQualityImage) {
            this.highQualityImage.style.display = 'block';
        }
        else if (this.lowQualityImage) {
            this.lowQualityImage.style.display = 'block';
        }
        if (this.pdfJsCanvas && this.pdfJsCanvas.parentNode) {
            this.container.removeChild(this.pdfJsCanvas);
        }
        if (this.textLayerDiv && this.textLayerDiv.parentNode) {
            this.container.removeChild(this.textLayerDiv);
        }
        if (this.annotationLayerDiv && this.annotationLayerDiv.parentNode) {
            this.container.removeChild(this.annotationLayerDiv);
        }
        switch (this.config.PDF_RENDER_STRATEGY) {
            case PDF_RENDER_STRATEGY.LOW_PREVIEW_HIGH_PREVIEW_NO_ANNOTATIONS:
                if (this.highQualityPreviewUrl && this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(this.renderLowAndHighQualityPreview(container, this.highQualityPreviewUrl, this.lowQualityPreviewUrl));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.HIGH_PREVIEW_NO_ANNOTATIONS:
                if (this.highQualityPreviewUrl) {
                    if (this.renderLowQualityPreviewPromise) {
                        this.assetViewerView.hideLoader();
                        // if a low res preview was already rendered, only append when high res preview is loaded
                        this.renderPromise = cancelable(this.loadHighQualityImage(this.highQualityPreviewUrl).then(() => {
                            if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                                this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                            }
                            container.appendChild(this.highQualityImage);
                            this.onPreviewRendered();
                            if (this.lowQualityImage) {
                                this.lowQualityImage.style.display = 'none';
                            }
                        }));
                        return this.renderPromise;
                    }
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(this.renderHighQualityPreview(container, this.highQualityPreviewUrl));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.LOW_PREVIEW_NO_ANNOTATIONS:
                if (this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(this.renderLowQualityPreview(container, this.lowQualityPreviewUrl));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.LOW_PREVIEW_HIGH_PREVIEW:
                if (this.highQualityPreviewUrl && this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderLowQualityPreview(container, this.lowQualityPreviewUrl).catch(this.onPreviewError),
                        this.loadHighQualityImage(this.highQualityPreviewUrl).then(() => {
                            if (!this.pdfCanvasLoaded) {
                                if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                                    this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                                }
                                container.appendChild(this.highQualityImage);
                                this.onPreviewRendered();
                                if (this.lowQualityImage) {
                                    this.lowQualityImage.style.display = 'none';
                                }
                            }
                        }),
                        this.renderAnnotationAndTextLayer(container)
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.HIGH_PREVIEW:
                if (this.highQualityPreviewUrl) {
                    if (this.renderLowQualityPreviewPromise) {
                        this.assetViewerView.hideLoader();
                        // if a low res preview was already rendered, only append when high res preview is loaded
                        this.renderPromise = cancelable(Promise.all([
                            this.loadHighQualityImage(this.highQualityPreviewUrl).then(() => {
                                if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                                    this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                                }
                                container.appendChild(this.highQualityImage);
                                this.onPreviewRendered();
                                if (this.lowQualityImage) {
                                    this.lowQualityImage.style.display = 'none';
                                }
                            }),
                            this.renderAnnotationAndTextLayer(container)
                        ]));
                        return this.renderPromise;
                    }
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderHighQualityPreview(container, this.highQualityPreviewUrl),
                        this.renderAnnotationAndTextLayer(container)
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.LOW_PREVIEW:
                if (this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderLowQualityPreview(container, this.lowQualityPreviewUrl),
                        this.renderAnnotationAndTextLayer(container)
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.LOW_PREVIEW_HIGH_PREVIEW_PDFJS:
                if (this.highQualityPreviewUrl && this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderLowQualityPreview(container, this.lowQualityPreviewUrl).catch(error => {
                            console.error(error);
                            return Promise.resolve();
                        }),
                        this.loadHighQualityImage(this.highQualityPreviewUrl)
                            .then(() => {
                            if (!this.pdfCanvasLoaded) {
                                if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                                    this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                                }
                                container.appendChild(this.highQualityImage);
                                this.onPreviewRendered();
                                this.lowQualityImage.style.display = 'none';
                            }
                        })
                            .catch(this.onPreviewError),
                        this.renderAnnotationAndTextLayer(container),
                        this.renderPdfJs(container).then(() => {
                            // container.removeChild(this.lowQualityImage);
                            // container.removeChild(this.highQualityImage);
                            if (this.lowQualityImage) {
                                this.lowQualityImage.style.display = 'none';
                            }
                            if (this.highQualityImage) {
                                this.highQualityImage.style.display = 'none';
                            }
                        })
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.HIGH_PREVIEW_PDFJS:
                if (this.highQualityPreviewUrl) {
                    if (this.renderLowQualityPreviewPromise) {
                        this.assetViewerView.hideLoader();
                        // if a low res preview was already rendered, only append when high res preview is loaded
                        this.renderPromise = cancelable(Promise.all([
                            this.loadHighQualityImage(this.highQualityPreviewUrl)
                                .then(() => {
                                if (this.assetViewerView.assetViewerItem.isDownloadable === false) {
                                    this.highQualityImage.addEventListener('contextmenu', e => e.preventDefault());
                                }
                                container.appendChild(this.highQualityImage);
                                this.onPreviewRendered();
                                if (this.lowQualityImage) {
                                    this.lowQualityImage.style.display = 'none';
                                }
                            })
                                .catch(this.onPreviewError),
                            this.renderAnnotationAndTextLayer(container),
                            this.renderPdfJs(container).then(() => {
                                // container.removeChild(this.lowQualityImage);
                                // container.removeChild(this.highQualityImage);
                                this.highQualityImage.style.display = 'none';
                            })
                        ]));
                        return this.renderPromise;
                    }
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderHighQualityPreview(container, this.highQualityPreviewUrl).catch(this.onPreviewError),
                        this.renderAnnotationAndTextLayer(container),
                        this.renderPdfJs(container).then(() => {
                            // container.removeChild(this.lowQualityImage);
                            // container.removeChild(this.highQualityImage);
                            this.highQualityImage.style.display = 'none';
                        })
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.LOW_PREVIEW_PDFJS:
                if (this.lowQualityPreviewUrl) {
                    this.assetViewerView.hideLoader();
                    this.renderPromise = cancelable(Promise.all([
                        this.renderLowQualityPreview(container, this.lowQualityPreviewUrl).catch(this.onPreviewError),
                        this.renderPdfJs(container)
                            .then(() => this.renderAnnotationAndTextLayer(container))
                            .then(() => {
                            // container.removeChild(this.lowQualityImage);
                            // container.removeChild(this.highQualityImage);
                            if (this.lowQualityImage) {
                                this.lowQualityImage.style.display = 'none';
                            }
                        })
                    ]));
                    return this.renderPromise;
                }
            case PDF_RENDER_STRATEGY.PDFJS:
                this.renderPromise = cancelable(Promise.all([this.renderAnnotationAndTextLayer(container), this.renderPdfJs(container)]));
                return this.renderPromise;
            default:
                return cancelable(Promise.reject(`no matching renderStrategy ${this.config.PDF_RENDER_STRATEGY}`));
        }
    }
    renderAnnotationAndTextLayer(container) {
        return this.loadPdfJsPage()
            .then(pdfPage => {
            this.renderAnnotationLayer(pdfPage, container);
            this.renderTextLayer(pdfPage, container, this.assetViewerView.page - 1);
        })
            .catch(this.onAnnotationLayerError);
    }
    onAnnotationLayerError(error) {
        if (error && error.status === 401 && typeof this.config.refreshToken === 'function') {
            // 401 should be handled by AssetViewerView
            throw error;
        }
        // Annotations and TextLayer failed, don't fallback
        console.error(error);
        return Promise.resolve();
    }
    onPreviewRendered() {
        this.assetViewerView.previewRenderState = RenderState.Finished;
        this.assetViewerView.eventBus.dispatch(new ViewerPreviewRenderedEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView) }));
    }
    onPreviewError(error) {
        this.assetViewerView.onPreviewError(error);
        // don't go to fallback presentation if only the preview failed
        return Promise.resolve();
    }
    renderTextLayer(pdfPage, container, pageIndex) {
        const textLayerDiv = document.createElement('div');
        const viewport = pdfPage.getViewport({ scale: 1 });
        if (this.annotationLayerDiv && this.annotationLayerDiv.parentNode) {
            // The annotation layer needs to stay on top.
            container.insertBefore(textLayerDiv, this.annotationLayerDiv);
        }
        else {
            container.appendChild(textLayerDiv);
        }
        this.textLayerDiv = textLayerDiv;
        this.resizeTextLayer();
        textLayerDiv.className = 'textLayer';
        this.getTextContentPromise = cancelable(pdfPage.getTextContent());
        this.getTextContentPromise
            .then(textContent => {
            const textLayerRenderParameters = {
                textContent: textContent,
                container: textLayerDiv,
                viewport: viewport,
                textDivs: []
            };
            this.textLayerRenderTask = window.pdfjsLib.renderTextLayer(textLayerRenderParameters);
            return this.textLayerRenderTask.promise;
        })
            .then(() => {
            this.resizeTextLayer();
        })
            .catch(error => {
            console.error(error);
        });
    }
    renderAnnotationLayer(pdfPage, container) {
        this.pdfJsLinkService.setDocument(this.pdfDocument);
        this.getAnnotationsPromise = cancelable(pdfPage.getAnnotations({ intent: 'display' }));
        this.getAnnotationsPromise
            .then(annotations => {
            if (annotations && annotations.length) {
                const annotationLayerDiv = (this.annotationLayerDiv = document.createElement('div'));
                annotationLayerDiv.className = 'annotationLayer';
                container.appendChild(annotationLayerDiv);
                annotationLayerDiv.addEventListener('click', this.onAnnotationLayerDivClick);
                const viewport = pdfPage.getViewport({ scale: 1 });
                const annotationLayerParameters = {
                    viewport: viewport.clone({
                        dontFlip: true
                    }),
                    div: annotationLayerDiv,
                    annotations,
                    page: pdfPage,
                    imageResourcesPath: '/images/',
                    renderForms: false,
                    linkService: this.pdfJsLinkService,
                    downloadManager: null,
                    annotationCanvasMap: this.annotationCanvasMap
                };
                window.pdfjsLib.AnnotationLayer.render(annotationLayerParameters);
                this.resizeAnnotationLayer();
            }
        })
            .catch(error => {
            console.error(error);
        });
    }
    renderPdfPage(pdfPage, container) {
        const pixelRatio = window.devicePixelRatio;
        const cappedDimensions = capDimensions(container.clientWidth * pixelRatio, container.clientHeight * pixelRatio, this.config.PDFJS_MAX_CANVAS_PIXELS);
        const scale = cappedDimensions.width / this.defaultViewport.width;
        const viewport = this.defaultViewport.clone({ scale });
        const canvas = (this.pdfJsCanvas = document.createElement('canvas'));
        const canvasContext = canvas.getContext('2d', {
            willReadFrequently: true
        });
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.classList.add(PDFJS_CANVAS_CLASS);
        let page = this.assetViewerView.assetViewerItem.page; // 1 - based;
        if (!page) {
            page = pdfPage.pageNumber;
        }
        const embeddedDataForPage = this.embeddedData ? this.embeddedData.find(item => item.page === page) : null;
        const renderContext = {
            canvasContext,
            viewport,
            annotationCanvasMap: this.annotationCanvasMap
        };
        this.renderTask = cancelable(Promise.resolve().then(() => {
            this.pdfJsRenderTask = pdfPage.render(renderContext).promise;
            return this.pdfJsRenderTask.catch(error => {
                if (error.type === 'canvas'
                // TODO: including RenderingCancelledException causes issues in JSDOM while testing
                // && error instanceof RenderingCancelledException
                ) {
                    console.log('error instanceof RenderingCancelledException');
                    return;
                }
                throw error;
            });
        }));
        return this.renderTask.then(result => {
            this.pdfCanvasLoaded = true;
            if (this.config.PDFJS_PINCH_ZOOM_ENABLED && !embeddedDataForPage) {
                if (this.pinchZoomLayer === null) {
                    this.pinchZoomLayer = new PfdJsPresentationRendererPinchZoomLayer(container, pdfPage);
                }
                else {
                    this.pinchZoomLayer.render();
                }
            }
            if (embeddedDataForPage) {
                this.embeddedMediaLayer = new PfdJsPresentationRendererEmbeddedMediaLayer(container, this.pdfDocument, pdfPage, this.assetViewerView, embeddedDataForPage.media, this.config.rootAssetViewer, this.config.PDFJS_MAX_CANVAS_PIXELS);
            }
            if (this.textLayerDiv && this.textLayerDiv.parentNode) {
                container.insertBefore(canvas, this.textLayerDiv);
                if (this.pinchZoomLayer) {
                    container.insertBefore(this.pinchZoomLayer.htmlElement, this.textLayerDiv);
                }
                if (this.embeddedMediaLayer) {
                    container.insertBefore(this.embeddedMediaLayer.htmlElement, this.textLayerDiv);
                }
            }
            else {
                container.appendChild(canvas);
                if (this.pinchZoomLayer) {
                    container.appendChild(this.pinchZoomLayer.htmlElement);
                }
                if (this.embeddedMediaLayer) {
                    container.appendChild(this.embeddedMediaLayer.htmlElement);
                }
            }
            return result;
        }, error => console.log(error));
    }
    resetPdf() {
        this.pdfDocumentPromise = null;
        this.pdfPagePromise = null;
    }
    resetPreview() {
        this.renderHighQualityPreviewPromise = null;
        this.renderLowQualityPreviewPromise = null;
        this.renderLowAndHighQualityPreviewPromise = null;
    }
    cancel() {
        this.containerWidth = null;
        this.containerHeight = null;
        if (this.pdfPagePromise) {
            this.pdfPagePromise.cancel();
            this.pdfPagePromise = null;
        }
        if (this.renderPromise) {
            this.renderPromise.cancel();
        }
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
        if (this.getTextContentPromise) {
            this.getTextContentPromise.cancel();
            this.getTextContentPromise = null;
        }
        if (this.textLayerRenderTask) {
            this.textLayerRenderTask.cancel();
            this.textLayerRenderTask = null;
        }
        if (this.getAnnotationsPromise) {
            this.getAnnotationsPromise.cancel();
            this.getAnnotationsPromise = null;
        }
        this.annotationCanvasMap = new Map();
        if (this.highQualityImage && !this.highQualityImageLoaded) {
            this.highQualityImage.src = '';
            if (this.highQualityImage.parentNode) {
                this.container.removeChild(this.highQualityImage);
            }
            this.highQualityImage.width = 0;
            this.highQualityImage.height = 0;
            this.highQualityImage = null;
            this.renderHighQualityPreviewPromise = null;
        }
        if (this.lowQualityImage && !this.lowQualityImageLoaded) {
            this.lowQualityImage.src = '';
            if (this.lowQualityImage.parentNode) {
                this.container.removeChild(this.lowQualityImage);
            }
            this.lowQualityImage.width = 0;
            this.lowQualityImage.height = 0;
            this.lowQualityImage = null;
            this.renderLowQualityPreviewPromise = null;
        }
    }
    destroy() {
        this.containerWidth = null;
        this.containerHeight = null;
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
        if (this.pdfPagePromise) {
            this.pdfPagePromise.cancel();
            this.pdfPagePromise = null;
        }
        if (this.renderPromise) {
            this.renderPromise.cancel();
        }
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
        if (this.getTextContentPromise) {
            this.getTextContentPromise.cancel();
            this.getTextContentPromise = null;
        }
        if (this.textLayerRenderTask) {
            this.textLayerRenderTask.cancel();
            this.textLayerRenderTask = null;
        }
        if (this.getAnnotationsPromise) {
            this.getAnnotationsPromise.cancel();
            this.getAnnotationsPromise = null;
        }
        this.annotationCanvasMap = new Map();
    }
    destroyFinal() {
        // TODO set width of all image to null, set images to null
        this.destroy();
        if (this.pinchZoomLayer) {
            this.pinchZoomLayer.destroy();
            this.pinchZoomLayer = null;
        }
        if (this.embeddedMediaLayer) {
            this.embeddedMediaLayer.destroy();
            this.embeddedMediaLayer = null;
        }
        if (this.pdfDocument) {
            this.pdfDocument.cleanup();
            this.pdfDocument.destroy();
            this.pdfDocument = null;
            // when the worker is passed to PDFJS.getDocument as a param, it's not destroyed by pdf.js
            if (this.assetViewerView.assetViewerItem.worker) {
                this.assetViewerView.assetViewerItem.worker.destroy();
                this.assetViewerView.assetViewerItem.worker = null;
            }
            this.assetViewerView.assetViewerItem.loadPdfPromise = null;
        }
        this.pdfDocumentPromise = null;
    }
    resizeTextLayer() {
        if (this.container && this.textLayerDiv) {
            const viewport = this.defaultViewport;
            const scale = this.container.clientWidth / viewport.width;
            this.textLayerDiv.style.cssText = `width: ${viewport.width}px; height: ${viewport.height}px; transform-origin: top left; transform: scale(${scale})`;
        }
    }
    resizeAnnotationLayer() {
        if (this.container && this.annotationLayerDiv) {
            this.annotationLayerDiv.style.transform = `scale(${this.container.clientWidth / this.defaultViewport.width})`;
        }
    }
    resize() {
        if (this.embeddedMediaLayer) {
            this.embeddedMediaLayer.resize();
        }
        this.resizeTextLayer();
        this.resizeAnnotationLayer();
    }
}
export class PfdJsPresentationRendererPinchZoomLayer {
    constructor(container, pdfPage) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-pdfjs-presentation-renderer__pinch-zoom-layer';
        this.renderId = 0;
        this.debouncedRenderMilliseconds = 100;
        this.minimumRequiredViewportScale = 2;
        this.debug = false;
        this.container = container;
        this.pdfPage = pdfPage;
        this.pdfDefaultViewport = this.pdfPage.getViewport({ scale: 1 });
        this.render = this.render.bind(this);
        this.debouncedRender = debounce(this.render.bind(this), this.debouncedRenderMilliseconds);
        this.onAnimationEnd = this.onAnimationEnd.bind(this);
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.addEventListeners();
        this.render();
    }
    addEventListeners() {
        this.htmlElement.addEventListener('animationend', this.onAnimationEnd);
        window.addEventListener('orientationchange', this.debouncedRender);
        window.addEventListener('resize', this.debouncedRender);
        window.addEventListener('scroll', this.debouncedRender);
    }
    removeEventListener() {
        window.removeEventListener('orientationchange', this.debouncedRender);
        window.removeEventListener('resize', this.debouncedRender);
        window.removeEventListener('scroll', this.debouncedRender);
    }
    render() {
        this.destroyRenderTask();
        const windowRect = this.fromDOMRect({
            top: 0,
            left: 0,
            bottom: window.innerHeight,
            right: window.innerWidth
        });
        const containerBoundingClientRect = this.container.getBoundingClientRect();
        const containerRect = this.fromDOMRect(containerBoundingClientRect);
        const borderWidth = (containerBoundingClientRect.width - this.container.clientWidth) / 2;
        containerRect.x1 += borderWidth;
        containerRect.y1 += borderWidth;
        containerRect.x2 -= borderWidth;
        containerRect.y2 -= borderWidth;
        const isIntersecting = this.intersect(windowRect, containerRect);
        const pdfWidth = this.pdfDefaultViewport.width;
        const pdfHeight = this.pdfDefaultViewport.height;
        let viewportX = 0;
        let viewportY = 0;
        let viewportWidth = 0;
        let viewportHeight = 0;
        let containerWidth = 0;
        let containerHeight = 0;
        let isInViewport = false;
        if (isIntersecting) {
            const intersectionRect = this.intersection(windowRect, containerRect);
            if (containerRect.x1 <= 0) {
                viewportX = -containerRect.x1;
            }
            if (containerRect.y1 <= 0) {
                viewportY = -containerRect.y1;
            }
            viewportWidth = intersectionRect.x2 - intersectionRect.x1;
            viewportHeight = intersectionRect.y2 - intersectionRect.y1;
            containerWidth = containerRect.x2 - containerRect.x1;
            containerHeight = containerRect.y2 - containerRect.y1;
            const paddingWidth = viewportWidth * 0.05;
            const paddingHeight = viewportHeight * 0.05;
            viewportX = Math.max(0, viewportX - paddingWidth);
            viewportY = Math.max(0, viewportY - paddingHeight);
            viewportWidth = Math.min(containerWidth, viewportWidth + paddingWidth * 2);
            viewportHeight = Math.min(containerHeight, viewportHeight + paddingHeight * 2);
            isInViewport = true;
        }
        const pixelRatio = window.devicePixelRatio;
        const viewportScale = containerWidth / viewportWidth;
        const viewportPdfScale = (containerWidth * pixelRatio) / pdfWidth;
        const scale = viewportScale * viewportPdfScale;
        const transformX = -((viewportX / containerWidth) * pdfWidth);
        const transformY = (viewportY / containerHeight) * pdfHeight;
        if (!isInViewport || viewportScale < this.minimumRequiredViewportScale) {
            this.destroyCanvases();
            return;
        }
        const canvas = document.createElement('canvas');
        const canvasContext = canvas.getContext('2d', {
            willReadFrequently: true
        });
        canvas.setAttribute('render-id', String(++this.renderId));
        canvas.className = 'fade-in';
        canvas.width = pdfWidth * (viewportWidth / containerWidth) * scale;
        canvas.height = pdfHeight * (viewportHeight / containerHeight) * scale;
        canvas.style.position = 'absolute';
        canvas.style.left = `${viewportX}px`;
        canvas.style.top = `${viewportY}px`;
        canvas.style.width = `${viewportWidth}px`;
        canvas.style.height = `${viewportHeight}px`;
        const viewport = this.pdfPage.getViewport({ scale });
        this.translate(viewport.transform, transformX, transformY);
        const renderContext = {
            canvasContext,
            viewport
        };
        this.renderTask = this.pdfPage.render(renderContext);
        const now = performance.now();
        this.renderTask.promise
            .then(pdfPage => {
            if (this.debug) {
                let x = 110;
                let y = 110;
                let p = 24;
                canvasContext.fillRect(100, 100, 500, 300);
                canvasContext.font = '20px monospace';
                canvasContext.fillStyle = 'white';
                canvasContext.fillText(`Render id: ${this.renderId}`, x, (y += p));
                canvasContext.fillText(`Render: ${(performance.now() - now).toFixed(2)}ms`, x, (y += p));
                canvasContext.fillText(`Viewport: ${viewportX.toFixed(0)}px ${viewportY.toFixed(0)}px ${viewportWidth.toFixed(0)}px x ${viewportHeight.toFixed(0)}px`, x, (y += p));
                canvasContext.fillText(`Pdf: ${pdfWidth.toFixed(0)}px x ${pdfHeight.toFixed(0)}px`, x, (y += p));
                canvasContext.fillText(`Container: ${containerWidth.toFixed(0)}px x ${containerHeight.toFixed(0)}px`, x, (y += p));
                canvasContext.fillText(`Viewport scale: ${viewportScale.toFixed(2)}`, x, (y += p));
                canvasContext.fillText(`Viewport pdf scale: ${viewportPdfScale.toFixed(2)}`, x, (y += p));
                canvasContext.fillText(`Scale: ${scale.toFixed(2)}`, x, (y += p));
                canvasContext.fillText(`Canvas: ${canvas.width.toFixed(2)}px x ${canvas.height.toFixed(2)}px`, x, (y += p));
            }
            this.htmlElement.appendChild(canvas);
            this.renderTask = null;
        })
            .catch(error => {
            if (error.type === 'canvas'
            // TODO: including RenderingCancelledException causes issues in JSDOM while testing
            // && error instanceof RenderingCancelledException
            ) {
                console.log('PfdJsPresentationRendererZoomLayer :: error instanceof RenderingCancelledException');
                return;
            }
            throw error;
        });
    }
    onAnimationEnd(event) {
        const renderId = parseInt(event.target.getAttribute('render-id'));
        event.target.className = '';
        if (renderId === this.renderId) {
            const elements = this.htmlElement.querySelectorAll(`canvas:not([render-id="${this.renderId}"])`);
            for (let i = 0; i < elements.length; i++) {
                const canvas = elements[i];
                canvas.parentNode.removeChild(canvas);
            }
        }
    }
    translate(matrix, x, y) {
        matrix[4] += matrix[0] * x + matrix[2] * y;
        matrix[5] += matrix[1] * x + matrix[3] * y;
    }
    intersect(a, b) {
        return a['x1'] < b['x2'] && a['x2'] > b['x1'] && a['y1'] < b['y2'] && a['y2'] > b['y1'];
    }
    intersection(a, b) {
        return {
            x1: Math.max(a['x1'], b['x1']),
            y1: Math.max(a['y1'], b['y1']),
            x2: Math.min(a['x2'], b['x2']),
            y2: Math.min(a['y2'], b['y2'])
        };
    }
    fromDOMRect(domRect) {
        return {
            x1: domRect['left'],
            y1: domRect['top'],
            x2: domRect['right'],
            y2: domRect['bottom']
        };
    }
    destroyCanvases() {
        const elements = this.htmlElement.querySelectorAll('canvas');
        for (let i = 0; i < elements.length; i++) {
            const canvas = elements[i];
            canvas.parentNode.removeChild(canvas);
        }
    }
    destroyRenderTask() {
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
    }
    destroy() {
        this.removeEventListener();
        this.destroyRenderTask();
        this.destroyCanvases();
    }
}
export class PfdJsPresentationRendererEmbeddedMediaLayer {
    constructor(container, pdfDoc, pdfPage, assetViewerView, media, rootAssetViewer, MAX_CANVAS_PIXELS) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-pdfjs-presentation-renderer__embedded-media-layer';
        this.CLASS_ANNOTATIONS_ENABLED = 'annotations-layer--enabled';
        this.debouncedOnMilliseconds = 100;
        this.isOn = false;
        this.mediaRenderers = [];
        this.extractedImages = [];
        this.container = container;
        this.pdfDoc = pdfDoc;
        this.pdfPage = pdfPage;
        this.assetViewerView = assetViewerView;
        this.rootAssetViewer = rootAssetViewer;
        this.MAX_CANVAS_PIXELS = MAX_CANVAS_PIXELS;
        this.media = this.orchestrateMedia(media);
        this.pdfDefaultViewport = this.pdfPage.getViewport({ scale: 1 });
        this.render = this.render.bind(this);
        this.onAssetViewerPublicRenderedViewFocus = this.onAssetViewerPublicRenderedViewFocus.bind(this);
        this.debouncedOn = debounce(this.on.bind(this), this.debouncedOnMilliseconds);
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.imageExtractor = new PfdJsPresentationRendererEmbeddedMediaSVGRendererImageExtractor(this.pdfDoc, this.pdfPage.pageNumber, this.media);
        this.addAnnotationsEnabledObserver();
        if (this.orchastrateOnFocus === true) {
            this.addViewFocusEventListener();
        }
        else {
            this.on();
        }
    }
    get scale() {
        return this.container.clientWidth / this.pdfDefaultViewport.width;
    }
    get containerWidth() {
        return this.container.clientWidth;
    }
    get containerHeight() {
        return this.container.clientHeight;
    }
    get pdfWidth() {
        return this.pdfDefaultViewport.width;
    }
    get pdfHeight() {
        return this.pdfDefaultViewport.height;
    }
    get orchastrateOnFocus() {
        return this.assetViewerView.config.VIEWER_LAYOUT === LAYOUT.HORIZONTAL;
    }
    render() {
        this.destroyRenderTask();
        const defer = PromiseHelper.defer();
        const pixelRatio = window.devicePixelRatio;
        const cappedDimensions = capDimensions(this.containerWidth * pixelRatio, this.containerHeight * pixelRatio, this.MAX_CANVAS_PIXELS);
        const scale = cappedDimensions.width / this.pdfWidth;
        const scaledViewport = this.pdfDefaultViewport.clone({ scale });
        const canvas = (this.canvas = document.createElement('canvas'));
        const canvasContext = canvas.getContext('2d', {
            willReadFrequently: true
        });
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        this.pdfDoc.getPage(this.pdfPage.pageNumber).then(pdfPage => {
            this.pdfPage = pdfPage;
            pdfPage.getOperatorList().then(pdfPageOperatorList => {
                this.pdfPageOperatorList = pdfPageOperatorList;
                const renderContext = {
                    canvasContext,
                    viewport: scaledViewport,
                    operatorList: this.injectEraseMediaOperations(this.pdfPageOperatorList, this.media.map(item => item.renderIndex))
                };
                this.renderTask = this.pdfPage.render(renderContext);
                this.renderTask.promise
                    .then(() => {
                    this.renderTask = null;
                    defer.resolve();
                })
                    .catch(error => {
                    if (error.type === 'canvas') {
                        console.log('PfdJsPresentationRendererEmbeddedMediaLayer :: error instanceof RenderingCancelledException');
                        return;
                    }
                    throw error;
                });
            });
        });
        return defer.promise;
    }
    renderMedia() {
        this.mediaRenderers = [];
        this.media
            .sort((itemA, itemB) => (itemA.renderIndex > itemB.renderIndex ? 1 : -1))
            .forEach((item) => {
            switch (item.type) {
                case EmbeddedDataMediaType.VIDEO:
                case EmbeddedDataMediaType.GIF:
                    const image = this.extractedImages.filter(image => image !== null).find(image => image.index === item.renderIndex);
                    const posterSrc = image ? image.element.getAttribute('xlink:href') : null;
                    const videoRenderer = new PfdJsPresentationRendererEmbeddedMediaLayerVideoRenderer(this.htmlElement, item, posterSrc, this.pdfWidth);
                    this.mediaRenderers.push(videoRenderer);
                    break;
            }
        });
    }
    addViewFocusEventListener() {
        this.rootAssetViewer.eventBus.on(ViewerEventType.PublicRenderedViewFocus, this.onAssetViewerPublicRenderedViewFocus);
    }
    removeViewFocusEventListener() {
        this.rootAssetViewer.eventBus.off(ViewerEventType.PublicRenderedViewFocus, this.onAssetViewerPublicRenderedViewFocus);
    }
    addAnnotationsEnabledObserver() {
        // Causes issues in ie so removed for now
        if (browser.ie) {
            return;
        }
        this.annotationsEnabledObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                const element = mutation.target;
                if (element.className.indexOf(this.CLASS_ANNOTATIONS_ENABLED) !== -1) {
                    this.onAnnotationsEnabled();
                }
            });
        });
        this.annotationsEnabledObserver.observe(this.container, {
            attributeFilter: ['class'],
            subtree: true
        });
    }
    removeAnnotationsEnabledObserver() {
        if (this.annotationsEnabledObserver) {
            this.annotationsEnabledObserver.disconnect();
        }
    }
    onAnnotationsEnabled() {
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.pause());
    }
    onAssetViewerPublicRenderedViewFocus(event) {
        if (event.payload.assetViewerView.id === this.assetViewerView.id) {
            this.debouncedOn();
        }
        else {
            this.off();
        }
    }
    on() {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.imageExtractor
            .render()
            .then((extractedImages) => {
            this.extractedImages = extractedImages;
            /*
            // BEGIN DEBUG
            console.log(`PAGE ${this.pdfPage.pageNumber}`);
            console.groupCollapsed(`Media`);
            console.log(this.media);
            console.groupEnd();
            console.groupCollapsed(`Extracted images`);
            console.log(this.extractedImages);
            console.groupEnd();
            */
            this.media.forEach((mediaItem, i) => {
                mediaItem.renderIndex = null;
                const extractedImage = this.extractedImages[i];
                if (extractedImage) {
                    mediaItem.renderIndex = extractedImage.index;
                }
            });
            // END DEBUG
            return this.render();
        })
            .then(() => {
            this.renderMedia();
            this.htmlElement.appendChild(this.canvas);
        });
    }
    off() {
        if (this.debouncedOn) {
            this.debouncedOn.cancel();
        }
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        this.destroyRenderTask();
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.destroy());
        this.htmlElement.innerHTML = '';
    }
    resize() {
        if (!this.isOn) {
            return;
        }
        this.render().then(() => {
            this.destroyCanvases();
            this.htmlElement.appendChild(this.canvas);
        });
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.resize());
    }
    orchestrateMedia(media) {
        // on ios autoplay will only work in certain cases for example if the media is muted
        if (browser.ios) {
            media.map(item => (item.playOptions.autoPlay = false));
            return media;
        }
        // when layout is vertical we are in a scrolling context we set all media to autoplay false
        if (this.orchastrateOnFocus === false) {
            media.map(item => (item.playOptions.autoPlay = false));
            return media;
        }
        // When we have a mixture of autoplay values we set all media to autoplay false
        const autoplayValues = media
            .map(item => item.playOptions.autoPlay)
            .filter((autoPlay, index, items) => items.indexOf(autoPlay) === index);
        if (autoplayValues.length > 1) {
            media.map(item => (item.playOptions.autoPlay = false));
        }
        return media;
    }
    injectEraseMediaOperations(operatorList, mediaRenderIndexes) {
        const imageFns = ['paintImageXObject', 'paintJpegXObject'];
        const OPS = window.pdfjsLib.OPS;
        const REVOPS = [];
        for (const op in OPS) {
            REVOPS[OPS[op]] = op;
        }
        /*
        // BEGIN DEBUG
        const operatorListReadable = operatorList.fnArray.map((fnId, i) => {
            return {
                name: REVOPS[fnId],
                index: i,
                fnId: fnId,
                fn: REVOPS[fnId],
                args: operatorList.argsArray[i]
            };
        });

        console.groupCollapsed(`Operations`);
        console.log(operatorListReadable);
        console.groupEnd();
        console.groupCollapsed(`Image operations`);
        console.log(operatorListReadable.filter(operation => imageFns.indexOf(operation.fn) !== -1));
        console.groupEnd();
        // END DEBUG
        */
        const mediaPaintOperatorIndexes = operatorList.fnArray
            .map((fnId, i) => {
            return {
                name: REVOPS[fnId],
                index: i,
                fnId: fnId,
                fn: REVOPS[fnId],
                args: operatorList.argsArray[i]
            };
        })
            .filter(operation => imageFns.indexOf(operation.fn) !== -1)
            .filter((operation, i) => mediaRenderIndexes.indexOf(i) !== -1)
            .map(operation => operation.index);
        mediaPaintOperatorIndexes.forEach((paintOperatorIndex, i) => {
            paintOperatorIndex += i;
            operatorList.argsArray.splice(paintOperatorIndex, 0, [[['BM', 'destination-out']]]);
            operatorList.fnArray.splice(paintOperatorIndex, 0, OPS.setGState);
        });
        return operatorList;
    }
    destroyCanvases() {
        const elements = this.htmlElement.querySelectorAll('canvas');
        for (let i = 0; i < elements.length; i++) {
            const canvas = elements[i];
            canvas.parentNode.removeChild(canvas);
        }
    }
    destroyRenderTask() {
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
    }
    destroy() {
        this.removeViewFocusEventListener();
        this.removeAnnotationsEnabledObserver();
        this.destroyRenderTask();
        if (this.imageExtractor) {
            this.imageExtractor.destroy();
        }
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.mediaRenderers.forEach(mediaRenderer => mediaRenderer.destroy());
        this.htmlElement.innerHTML = '';
    }
}
export class PfdJsPresentationRendererEmbeddedMediaLayerVideoRenderer {
    constructor(container, media, posterSrc, pdfDefaultWidth) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-pdfjs-presentation-renderer__embedded-media-layer__video-renderer';
        this.videoContainerElement = null;
        this.videoElement = null;
        this.imageElement = null;
        this.intersectionElement = null;
        this.container = container;
        this.media = media;
        this.posterSrc = posterSrc;
        this.pdfDefaultWidth = pdfDefaultWidth;
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.className;
        this.videoContainerElement = document.createElement('div');
        this.videoElement = document.createElement('video');
        this.intersectionElement = document.createElement('div');
        this.addVideoEventListeners();
        this.render();
    }
    get scale() {
        return this.containerWidth / this.pdfDefaultWidth;
    }
    get containerWidth() {
        return this.container.clientWidth;
    }
    get containerHeight() {
        return this.container.clientHeight;
    }
    get intersectionRect() {
        const htmlElementRect = this.htmlElement.getBoundingClientRect();
        const videoContainerElementRect = this.videoContainerElement.getBoundingClientRect();
        return this.intersection(htmlElementRect, videoContainerElementRect);
    }
    addVideoEventListeners() {
        this.oneVideoPlay = this.oneVideoPlay.bind(this);
        this.videoElement.addEventListener('play', this.oneVideoPlay);
    }
    removeVideoEventListeners() {
        this.videoElement.removeEventListener('play', this.oneVideoPlay);
    }
    oneVideoPlay() {
        this.load();
        this.videoElement.classList.remove('off');
        if (this.imageElement && this.imageElement.parentNode) {
            this.imageElement.parentNode.removeChild(this.imageElement);
        }
        this.videoElement.removeEventListener('play', this.oneVideoPlay);
    }
    render() {
        const videoElement = this.videoElement;
        videoElement.className = `${this.className}__video`;
        videoElement.controls = false;
        videoElement.width = this.media.size.width;
        videoElement.height = this.media.size.height;
        videoElement.autoplay = this.media.playOptions.autoPlay;
        videoElement.loop = this.media.playOptions.repeat !== EmbeddedDataMediaPlayOptionsRepeat.NONE;
        videoElement.playsInline = true;
        if (this.posterSrc) {
            this.imageElement = document.createElement('img');
            this.imageElement.className = `${this.className}__poster`;
            this.imageElement.src = this.posterSrc;
            this.htmlElement.appendChild(this.imageElement);
        }
        this.container.appendChild(this.htmlElement);
        const trim = this.media.playOptions.trim;
        const trimStart = trim && trim.start ? millisecondsToSeconds(trim.start) : 0;
        const trimEnd = trim && trim.end ? millisecondsToSeconds(trim.end) : 0;
        const maxVolume = mapRange(this.media.playOptions.volume, 0, 100, 0, 1);
        const repeatCount = isNaN(parseInt(this.media.playOptions.repeat)) ? null : parseInt(this.media.playOptions.repeat);
        this.controls = new PfdJsPresentationRendererEmbeddedMediaLayerVideoRendererControls(this.container, videoElement, trimStart, trimEnd, maxVolume, repeatCount);
        if (videoElement.autoplay) {
            this.load();
        }
        this.videoContainerElement.appendChild(videoElement);
        this.htmlElement.appendChild(this.videoContainerElement);
        this.resize();
    }
    load() {
        if (this.videoElement.src) {
            return;
        }
        this.videoElement.src = this.media.uri.processed || this.media.uri.converted;
    }
    pause() {
        this.videoElement.pause();
    }
    resize() {
        const mediaSize = this.media.size;
        const arrangement = this.media.arrangement;
        const size = arrangement.size;
        const offset = arrangement.offset;
        const crop = arrangement.crop;
        const flip = arrangement.flip;
        const flipVertical = flip.vertical === true ? -1 : 1;
        const flipHorizontal = flip.horizontal === true ? -1 : 1;
        let top = 0;
        let left = 0;
        let height = 100;
        let width = 100;
        if (crop) {
            width =
                (mediaSize.width / (mediaSize.width - mediaSize.width * (crop.left / 100) - mediaSize.width * (crop.right / 100))) * 100;
            height =
                (mediaSize.height / (mediaSize.height - mediaSize.height * (crop.top / 100) - mediaSize.height * (crop.bottom / 100))) *
                    100;
            top = -((height * crop.top) / 100);
            left = -((width * crop.left) / 100);
        }
        this.videoContainerElement.style.cssText = `position: absolute; top:${top}%; left:${left}%; width:${width}%; height:${height}%;`;
        if (this.imageElement) {
            this.htmlElement.style.cssText = `width:${size.width * this.scale}px; height:${size.height *
                this.scale}px; transform: translate(${offset.x * this.scale}px, ${offset.y *
                this.scale}px) rotate(${0}deg) scale(${flipHorizontal}, ${flipVertical})`;
            const intersectionRect = {
                top: this.intersectionRect.top,
                right: this.intersectionRect.right,
                bottom: this.intersectionRect.bottom,
                left: this.intersectionRect.left,
                height: this.intersectionRect.height,
                width: this.intersectionRect.width
            };
            const htmlElementRect = this.htmlElement.getBoundingClientRect();
            intersectionRect.top -= htmlElementRect.top;
            intersectionRect.left -= htmlElementRect.left;
            this.imageElement.style.cssText = `position: absolute; top:${intersectionRect.top}px; left:${intersectionRect.left}px; width:${intersectionRect.width}px; height:${intersectionRect.height}px`;
        }
        this.htmlElement.style.cssText = `width:${size.width * this.scale}px; height:${size.height *
            this.scale}px; transform: translate(${offset.x * this.scale}px, ${offset.y * this.scale}px) rotate(${arrangement.rotation}deg) scale(${flipHorizontal}, ${flipVertical})`;
        const scaleX = this.videoContainerElement.clientWidth / mediaSize.width;
        const scaleY = this.videoContainerElement.clientHeight / mediaSize.height;
        this.videoElement.style.cssText = `position: absolute; top:0; left:0; transform: scale(${scaleX}, ${scaleY});`;
        this.controls.resize(this.intersectionRect);
    }
    intersection(a, b) {
        const left = Math.max(a.left, b.left);
        const top = Math.max(a.top, b.top);
        const right = Math.min(a.right, b.right);
        const bottom = Math.min(a.bottom, b.bottom);
        const width = right - left;
        const height = bottom - top;
        return {
            left,
            top,
            right,
            bottom,
            width,
            height
        };
    }
    destroy() {
        this.removeVideoEventListeners();
        if (this.controls) {
            this.controls.destroy();
        }
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
        }
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.htmlElement.innerHTML = '';
    }
}
export class PfdJsPresentationRendererEmbeddedMediaLayerVideoRendererControls {
    constructor(container, videoElement, trimStart = null, trimEnd = null, maxVolume = 100, repeatCount = null) {
        this.container = null;
        this.htmlElement = null;
        this.className = 'sv-pdfjs-presentation-renderer__embedded-media-layer__video-renderer-controls';
        this.videoElement = null;
        this.videoDuration = null;
        this.maxVolume = null; // 0 - 1
        this.repeatCount = null;
        this.startTime = null;
        this.endTime = null;
        this.duration = null;
        this.isLoadingMetaData = true;
        this.isBuffering = true;
        this.isScrubbing = false;
        this.isPlaying = false;
        this.wasPlaying = null;
        this.isMuted = false;
        this.playedCount = 0;
        this.previousCurrentTime = null;
        this.debouncedVideoWaitingMilliseconds = 100;
        this.MINI_BREAKPOINT = 300;
        this.TINY_BREAKPOINT = 150;
        this.container = container;
        this.videoElement = videoElement;
        this.trimStart = trimStart;
        this.trimEnd = trimEnd;
        this.maxVolume = this.videoElement.volume = maxVolume;
        this.repeatCount = repeatCount;
        this.isPlaying = this.videoElement.autoplay;
        this.startTime = trimStart;
        // when video loops waiting event gets triggered which shows buffering for a fraction of a second, so we debounce it
        this.debouncedVideoWaiting = debounce(this.onVideoWaiting.bind(this), this.debouncedVideoWaitingMilliseconds);
        this.render();
        this.update();
        this.addEventListeners();
    }
    get currentTime() {
        return mapRange(this.videoCurrentTime, this.startTime, this.endTime, 0, this.duration);
    }
    get currentTimeFormatted() {
        return this.secondsToMinutesSeconds(this.currentTime);
    }
    get remainingTime() {
        return mapRange(this.videoCurrentTime, this.startTime, this.endTime, this.duration, 0);
    }
    get remainingTimeFormatted() {
        return `-${this.secondsToMinutesSeconds(this.remainingTime)}`;
    }
    get videoCurrentTime() {
        return clamp(this.videoElement.currentTime, this.startTime, this.endTime);
    }
    get classNames() {
        const classNames = [this.className];
        if (this.isLoadingMetaData) {
            classNames.push(`is-loading-metadata`);
        }
        if (this.isBuffering && this.isPlaying) {
            classNames.push(`is-buffering`);
        }
        if (this.width <= this.TINY_BREAKPOINT) {
            classNames.push(`tiny`);
        }
        else if (this.width <= this.MINI_BREAKPOINT) {
            classNames.push(`mini`);
        }
        if (browser.touch) {
            classNames.push(`is-touch`);
        }
        return classNames.join(' ');
    }
    get width() {
        return this.htmlElement.clientWidth;
    }
    render() {
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = this.classNames;
        this.htmlElement.innerHTML = `
    <div class="click-area" data-action='play'></div>
    <div class="click-area" data-action='pause'></div>
    <button class="play" data-action='play'>
        <svg viewBox="0 0 102 102" xmlns="http://www.w3.org/2000/svg"><g transform="translate(1 1)" fill="none" fill-rule="evenodd"><circle fill="#FFF" cx="50" cy="50" r="50"/><path fill="#000" d="M63.78 50L41.732 64.567V35.433z"/></g></svg>
    </button>
    <div class="buffering" data-action='buffering'>
        <svg class="circular" viewBox="25 25 50 50">
            <circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10"/>
        </svg>
    </div>
    <div class="toolbar">
        <button data-action='play'>
            <svg fill="currentColor" viewBox="0 0 18 18">
                <path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
            </svg>
        </button>
        <button data-action='pause'>
            <svg fill="currentColor" viewBox="0 0 18 18">
                <path d="M6 1H3c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1zM12 1c-.6 0-1 .4-1 1v14c0 .6.4 1 1 1h3c.6 0 1-.4 1-1V2c0-.6-.4-1-1-1h-3z"></path>
            </svg>
        </button>
        <div data-id="currentTime" class="time"></div>
        <input data-id="progress" type="range" min="0" max="1" step="0.01" value="0" autocomplete="off" role="slider">
        <div data-id="remainingTime" class="time"></div>
        ${(maxVolume => {
            if (maxVolume === 0) {
                return ``;
            }
            return `
            <button data-action='mute'>
                <svg fill="currentColor" viewBox="0 0 18 18">
                    <path d="M15.6 3.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4C15.4 5.9 16 7.4 16 9c0 1.6-.6 3.1-1.8 4.3-.4.4-.4 1 0 1.4.2.2.5.3.7.3.3 0 .5-.1.7-.3C17.1 13.2 18 11.2 18 9s-.9-4.2-2.4-5.7z"></path><path d="M11.282 5.282a.909.909 0 0 0 0 1.316c.735.735.995 1.458.995 2.402 0 .936-.425 1.917-.995 2.487a.909.909 0 0 0 0 1.316c.145.145.636.262 1.018.156a.725.725 0 0 0 .298-.156C13.773 11.733 14.13 10.16 14.13 9c0-.17-.002-.34-.011-.51-.053-.992-.319-2.005-1.522-3.208a.909.909 0 0 0-1.316 0zM3.786 6.008H.714C.286 6.008 0 6.31 0 6.76v4.512c0 .452.286.752.714.752h3.072l4.071 3.858c.5.3 1.143 0 1.143-.602V2.752c0-.601-.643-.977-1.143-.601L3.786 6.008z"></path>
                </svg>
            </button>
            <button data-action='unmute'>
                <svg fill="currentColor" viewBox="0 0 18 18">
                    <path d="M12.4 12.5l2.1-2.1 2.1 2.1 1.4-1.4L15.9 9 18 6.9l-1.4-1.4-2.1 2.1-2.1-2.1L11 6.9 13.1 9 11 11.1zM3.786 6.008H.714C.286 6.008 0 6.31 0 6.76v4.512c0 .452.286.752.714.752h3.072l4.071 3.858c.5.3 1.143 0 1.143-.602V2.752c0-.601-.643-.977-1.143-.601L3.786 6.008z"></path>
                </svg>
            </button>`;
        })(this.maxVolume)}
        <button data-action="enterFullscreen">
            <svg fill="currentColor" viewBox="0 0 18 18"><path d="M10 3h3.6l-4 4L11 8.4l4-4V8h2V1h-7zM7 9.6l-4 4V10H1v7h7v-2H4.4l4-4z"></path></svg>
        </button>
        <button data-action="exitFullscreen">
            <svg fill="currentColor" viewBox="0 0 18 18"><path fill="currentColor" d="M1 12h3.6l-4 4L2 17.4l4-4V17h2v-7H1zM16 .6l-4 4V1h-2v7h7V6h-3.6l4-4z"></path></svg>
        </button>
    </div>`;
        this.progressElement = this.htmlElement.querySelector(`[data-id='progress']`);
        this.currentTimeElement = this.htmlElement.querySelector(`[data-id='currentTime']`);
        this.remainingTimeElement = this.htmlElement.querySelector(`[data-id='remainingTime']`);
        this.container.appendChild(this.htmlElement);
    }
    update() {
        this.htmlElement.className = this.classNames;
        const actions = ['play', 'pause', 'mute', 'unmute', 'enterFullscreen', 'exitFullscreen'];
        const currentActions = [this.isPlaying ? 'pause' : 'play', this.isMuted ? 'unmute' : 'mute'];
        actions.forEach(action => {
            const elements = this.htmlElement.querySelectorAll(`[data-action='${action}']`);
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i];
                currentActions.indexOf(action) === -1 ? element.classList.add('off') : element.classList.remove('off');
            }
        });
    }
    updateVideoDuration(duration) {
        this.videoDuration = duration;
        this.endTime = this.videoDuration - this.trimEnd;
        this.duration = this.endTime - this.startTime;
        this.progressElement.setAttribute('min', String(this.startTime));
        this.progressElement.setAttribute('max', String(this.endTime));
    }
    addEventListeners() {
        this.onClick = this.onClick.bind(this);
        this.onVideoPlay = this.onVideoPlay.bind(this);
        this.onVideoPause = this.onVideoPause.bind(this);
        this.onVideoWaiting = this.onVideoWaiting.bind(this);
        this.onVideoPlaying = this.onVideoPlaying.bind(this);
        this.onVideoTimeUpdate = this.onVideoTimeUpdate.bind(this);
        this.onVideoLoadedMetaData = this.onVideoLoadedMetaData.bind(this);
        this.htmlElement.addEventListener('click', this.onClick);
        this.onProgressInput = this.onProgressInput.bind(this);
        this.onProgressChange = this.onProgressChange.bind(this);
        this.videoElement.addEventListener('play', this.onVideoPlay);
        this.videoElement.addEventListener('pause', this.onVideoPause);
        this.videoElement.addEventListener('waiting', this.debouncedVideoWaiting);
        this.videoElement.addEventListener('playing', this.onVideoPlaying);
        this.videoElement.addEventListener('timeupdate', this.onVideoTimeUpdate);
        this.videoElement.addEventListener('loadedmetadata', this.onVideoLoadedMetaData);
        this.progressElement.addEventListener('input', this.onProgressInput);
        this.progressElement.addEventListener('change', this.onProgressChange);
    }
    removeEventListeners() {
        this.htmlElement.removeEventListener('click', this.onClick);
        this.videoElement.removeEventListener('play', this.onVideoPlay);
        this.videoElement.removeEventListener('pause', this.onVideoPause);
        this.videoElement.removeEventListener('waiting', this.onVideoWaiting);
        this.videoElement.removeEventListener('playing', this.onVideoPlaying);
        this.videoElement.removeEventListener('timeupdate', this.onVideoTimeUpdate);
        this.videoElement.removeEventListener('loadedmetadata', this.onVideoLoadedMetaData);
        this.progressElement.removeEventListener('input', this.onProgressInput);
        this.progressElement.removeEventListener('change', this.onProgressChange);
    }
    onClick(e) {
        const target = e.target;
        const action = target.getAttribute('data-action');
        switch (action) {
            case 'play':
                this.videoElement.play();
                break;
            case 'pause':
                this.videoElement.pause();
                break;
            case 'mute':
                this.isMuted = true;
                this.videoElement.volume = 0;
                break;
            case 'unmute':
                this.isMuted = false;
                this.videoElement.volume = this.maxVolume;
                break;
        }
        this.update();
    }
    onVideoLoadedMetaData() {
        this.isLoadingMetaData = false;
        this.videoElement.currentTime = this.startTime;
        this.updateVideoDuration(this.videoElement.duration);
        /*
        console.log(
            `videoDuration: ${this.videoDuration} trimStart: ${this.trimStart} trimEnd: ${this.trimEnd} startTime: ${
                this.startTime
            } endTime: ${this.endTime} duration: ${this.duration} repeatCount: ${this.repeatCount}`
        );
        */
        this.update();
    }
    onVideoTimeUpdate() {
        this.debouncedVideoWaiting.cancel();
        this.isBuffering = false;
        this.progressElement.value = String(this.videoCurrentTime);
        this.currentTimeElement.innerHTML = this.currentTimeFormatted;
        this.remainingTimeElement.innerHTML = this.remainingTimeFormatted;
        // When using hls.js the duration can change on the fly as streams can change
        if (this.videoDuration !== this.videoElement.duration) {
            this.updateVideoDuration(this.videoElement.duration);
        }
        // Repeat logic
        if (this.repeatCount !== null && this.isScrubbing === false && this.isPlaying) {
            if (this.videoElement.currentTime < this.previousCurrentTime) {
                this.playedCount++;
                if (this.playedCount === this.repeatCount) {
                    this.videoElement.pause();
                    this.playedCount = 0;
                }
            }
        }
        if (this.isScrubbing === false && this.isPlaying) {
            // If video is dynamically trimmed we need to watch for loop and played manually 😞
            if (this.trimStart > 0 || this.trimEnd > 0) {
                if (this.videoElement.loop && Math.floor(this.videoElement.currentTime) >= Math.floor(this.endTime)) {
                    this.videoElement.currentTime = this.startTime;
                }
                else if (this.videoElement.currentTime >= this.endTime) {
                    this.videoElement.pause();
                    this.videoElement.currentTime = this.endTime;
                }
            }
        }
        this.previousCurrentTime = this.videoElement.currentTime;
        this.update();
    }
    onVideoPlay(e) {
        this.isPlaying = true;
        if (Math.floor(this.videoElement.currentTime) >= Math.floor(this.endTime)) {
            this.videoElement.currentTime = this.startTime;
        }
        this.update();
    }
    onVideoPause(e) {
        this.isPlaying = false;
        this.update();
    }
    onVideoWaiting(e) {
        this.isBuffering = true;
        this.update();
    }
    onVideoPlaying(e) {
        this.debouncedVideoWaiting.cancel();
        this.isBuffering = false;
        this.update();
    }
    onProgressInput(e) {
        if (this.isScrubbing === false) {
            this.isScrubbing = true;
            this.videoElement.pause();
            this.wasPlaying = this.isPlaying;
        }
        const target = e.target;
        this.videoElement.currentTime = parseFloat(target.value);
    }
    onProgressChange(e) {
        this.isScrubbing = false;
        if (this.wasPlaying) {
            this.videoElement.play();
        }
        this.wasPlaying = null;
    }
    secondsToMinutesSeconds(seconds) {
        return Math.floor(seconds / 60) + ':' + ('0' + Math.floor(seconds % 60)).slice(-2);
    }
    resize(videoClientRect) {
        const videoRect = this.fromClientRect(videoClientRect);
        const containerRect = this.fromClientRect(this.container.getBoundingClientRect());
        const intersectionRect = this.intersection(containerRect, videoRect);
        intersectionRect.x1 -= containerRect.x1;
        intersectionRect.y1 -= containerRect.y1;
        intersectionRect.x2 -= containerRect.x1;
        intersectionRect.y2 -= containerRect.y1;
        this.htmlElement.style.cssText = `position: absolute; top: ${intersectionRect.y1}px; left: ${intersectionRect.x1}px; width: ${intersectionRect.x2 - intersectionRect.x1}px; height: ${intersectionRect.y2 - intersectionRect.y1}px;`;
        this.update();
    }
    intersection(a, b) {
        return {
            x1: Math.max(a.x1, b.x1),
            y1: Math.max(a.y1, b.y1),
            x2: Math.min(a.x2, b.x2),
            y2: Math.min(a.y2, b.y2)
        };
    }
    fromClientRect(clientRect) {
        return {
            x1: clientRect.left,
            y1: clientRect.top,
            x2: clientRect.right,
            y2: clientRect.bottom
        };
    }
    destroy() {
        this.removeEventListeners();
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.htmlElement.innerHTML = '';
    }
}
export class PfdJsPresentationRendererEmbeddedMediaSVGRendererImageExtractor {
    constructor(pdfDoc, pdfPageNumber, media, BEST_MATCH_BOUNDS_PRECISION = 5) {
        this.htmlElement = null;
        this.className = 'sv-pdfjs-presentation-renderer__embedded-media-svg-renderer-image-extractor';
        this.pdfDoc = pdfDoc;
        this.pdfPageNumber = pdfPageNumber;
        this.media = media;
        this.BEST_MATCH_BOUNDS_PRECISION = BEST_MATCH_BOUNDS_PRECISION;
        this.htmlElement = document.createElement(`div`);
        this.htmlElement.className = this.className;
        this.htmlElement.style.cssText = `position: absolute; top: 0; left: 0; pointer-events: none; opacity: 0;`;
    }
    render() {
        const defer = PromiseHelper.defer();
        this.htmlElement.innerHTML = ``;
        this.renderPromise = cancelable(this.pdfDoc.getPage(this.pdfPageNumber).then(pdfPage => {
            this.pdfPage = pdfPage;
            pdfPage.getOperatorList().then(pdfPageOperatorList => {
                this.pdfPageOperatorList = pdfPageOperatorList;
                document.body.appendChild(this.htmlElement);
                const htmlElementRect = this.htmlElement.getBoundingClientRect();
                const svgGraphics = new window.pdfjsLib.SVGGraphics(this.pdfPage.commonObjs, this.pdfPage.objs);
                return svgGraphics.getSVG(this.pdfPageOperatorList, this.pdfPage.getViewport({ scale: 1 })).then(svg => {
                    this.htmlElement.appendChild(svg);
                    const svgImages = [].slice.call(svg.querySelectorAll('image'));
                    const serial = funcs => funcs.reduce((promise, func) => promise.then(result => func().then(Array.prototype.concat.bind(result))), Promise.resolve([]));
                    const tasks = this.media.map((mediaItem) => () => {
                        return new Promise((resolve, reject) => {
                            const positionElement = document.createElement('div');
                            const positionContentElement = document.createElement('div');
                            const intersectionElement = document.createElement('div');
                            const arrangement = mediaItem.arrangement;
                            const size = arrangement.size;
                            const offset = arrangement.offset;
                            const mediaSize = mediaItem.size;
                            const crop = arrangement.crop;
                            positionElement.style.cssText = `position: absolute; top: 0; left: 0; width:${size.width}px; height:${size.height}px; transform: translate(${offset.x}px, ${offset.y}px) rotate(0deg);`;
                            let top = 0;
                            let left = 0;
                            let height = 100;
                            let width = 100;
                            if (crop) {
                                width =
                                    (mediaSize.width /
                                        (mediaSize.width -
                                            mediaSize.width * (crop.left / 100) -
                                            mediaSize.width * (crop.right / 100))) *
                                        100;
                                height =
                                    (mediaSize.height /
                                        (mediaSize.height -
                                            mediaSize.height * (crop.top / 100) -
                                            mediaSize.height * (crop.bottom / 100))) *
                                        100;
                                top = -((height * crop.top) / 100);
                                left = -((width * crop.left) / 100);
                            }
                            positionContentElement.style.cssText = `position: absolute; top:${top}%; left:${left}%; width:${width}%; height:${height}%;`;
                            positionElement.appendChild(positionContentElement);
                            this.htmlElement.appendChild(positionElement);
                            const positionRect = positionElement.getBoundingClientRect();
                            const positionContentRect = positionContentElement.getBoundingClientRect();
                            const positionContentIntersectionRect = this.intersection(positionRect, positionContentRect);
                            positionContentIntersectionRect.top -= offset.y;
                            positionContentIntersectionRect.left -= offset.x;
                            positionContentIntersectionRect.top -= htmlElementRect.top;
                            positionContentIntersectionRect.left -= htmlElementRect.left;
                            intersectionElement.style.cssText = `position: absolute; top:${positionContentIntersectionRect.top}px; left:${positionContentIntersectionRect.left}px; width:${positionContentIntersectionRect.width}px; height:${positionContentIntersectionRect.height}px; background:#0FF;`;
                            positionElement.appendChild(intersectionElement);
                            positionElement.style.cssText = `position: absolute; top: 0; left: 0; width:${size.width}px; height:${size.height}px; transform: translate(${offset.x}px, ${offset.y}px) rotate(${arrangement.rotation}deg);`;
                            const intersectionRect = intersectionElement.getBoundingClientRect();
                            let extractedImages = svgImages
                                .map((svgImage, svgImageIndex) => {
                                const svgImageRect = svgImage.getBoundingClientRect();
                                const xDiff = Math.abs(intersectionRect.left - svgImageRect.left);
                                const yDiff = Math.abs(intersectionRect.top - svgImageRect.top);
                                const wDiff = Math.abs(intersectionRect.width - svgImageRect.width);
                                const hDiff = Math.abs(intersectionRect.height - svgImageRect.height);
                                const boundsDiff = parseFloat((xDiff + yDiff + wDiff + hDiff).toFixed(1));
                                return {
                                    index: svgImageIndex,
                                    element: svgImage,
                                    boundsDiff: boundsDiff
                                };
                            })
                                .sort((imageA, imageB) => {
                                if (imageA.boundsDiff < imageB.boundsDiff) {
                                    return -1;
                                }
                                if (imageA.boundsDiff > imageB.boundsDiff) {
                                    return 1;
                                }
                                if (imageA.index < imageB.index) {
                                    return 1;
                                }
                                if (imageA.index > imageB.index) {
                                    return -1;
                                }
                                return 0;
                            })
                                .filter(image => image.boundsDiff <= this.BEST_MATCH_BOUNDS_PRECISION);
                            if (extractedImages.length > 1 && extractedImages[0].boundsDiff === extractedImages[1].boundsDiff) {
                                const tasks = extractedImages.map(extractedImage => () => {
                                    return new Promise((resolve, reject) => {
                                        const image = new Image();
                                        const canvas = document.createElement('canvas');
                                        const context = canvas.getContext('2d', {
                                            willReadFrequently: true
                                        });
                                        image.src = extractedImage.element.getAttribute('xlink:href');
                                        image.onload = () => {
                                            canvas.width = 50;
                                            canvas.height = 50;
                                            context.drawImage(image, 0, 0, canvas.width, canvas.height);
                                            const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
                                            let transparency = false;
                                            for (let i = 0; i < imageData.length; i += 4) {
                                                if (imageData[i + 3] < 255) {
                                                    transparency = true;
                                                    break;
                                                }
                                            }
                                            extractedImage.transparency = transparency;
                                            this.htmlElement.appendChild(canvas);
                                            resolve(extractedImage);
                                        };
                                    });
                                });
                                serial(tasks).then(result => {
                                    extractedImages = extractedImages.filter(extractedImage => extractedImage.transparency === false);
                                    resolve(extractedImages[0] || null);
                                });
                            }
                            else {
                                resolve(extractedImages[0] || null);
                            }
                        });
                    });
                    return serial(tasks).then(results => {
                        this.htmlElement.innerHTML = ``;
                        document.body.removeChild(this.htmlElement);
                        defer.resolve(results);
                    });
                });
            });
        }));
        return cancelable(defer.promise);
    }
    intersection(a, b) {
        const left = Math.max(a.left, b.left);
        const top = Math.max(a.top, b.top);
        const right = Math.min(a.right, b.right);
        const bottom = Math.min(a.bottom, b.bottom);
        const width = right - left;
        const height = bottom - top;
        return {
            left,
            top,
            right,
            bottom,
            width,
            height
        };
    }
    destroy() {
        if (this.renderPromise) {
            this.renderPromise.cancel();
            this.renderPromise = null;
        }
        if (this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }
        this.htmlElement.innerHTML = ``;
    }
}
//# sourceMappingURL=PdfJsPresentationRenderer.js.map